'use client';

import { useEffect, useState } from "react";
import { Activity, Bot, Dot, Radio } from "lucide-react";

type FeedItem = {
  id: number;
  crawler: string;
  site: string;
  page: string;
  secondsAgo: number;
};

const CRAWLERS = [
  "GPTBot",
  "ClaudeBot",
  "PerplexityBot",
  "Google-Extended",
  "OAI-SearchBot",
  "ChatGPT-User",
  "CCBot",
  "Amazonbot",
  "Applebot-Extended",
  "Bytespider",
];

const SITES = [
  "B2B SaaS docs",
  "ecommerce catalog",
  "startup pricing page",
  "help center",
  "developer docs",
  "product marketing site",
  "agency knowledge base",
  "support portal",
];

const PAGES = [
  "/pricing",
  "/docs/proxy-free-setup",
  "/help/returns-policy",
  "/blog/ai-visibility-guide",
  "/faq/shipping",
  "/features",
  "/integrations/intercom",
  "/compare/cloudflare",
];

function buildItem(id: number): FeedItem {
  return {
    id,
    crawler: CRAWLERS[id % CRAWLERS.length],
    site: SITES[id % SITES.length],
    page: PAGES[id % PAGES.length],
    secondsAgo: (id % 4) * 7 + 3,
  };
}

const INITIAL_ITEMS = [31, 28, 25, 22, 19].map(buildItem);

export function RealtimeCrawlerFeed() {
  const [items, setItems] = useState<FeedItem[]>(INITIAL_ITEMS);

  useEffect(() => {
    let nextId = 34;

    const tick = window.setInterval(() => {
      setItems((current) => current.map((item) => ({ ...item, secondsAgo: item.secondsAgo + 3 })));
    }, 3000);

    const inject = window.setInterval(() => {
      setItems((current) => [buildItem(nextId), ...current.slice(0, 4)]);
      nextId += 1;
    }, 3800);

    return () => {
      window.clearInterval(tick);
      window.clearInterval(inject);
    };
  }, []);

  return (
    <section className="py-20 bg-white border-y border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
          <div>
            <p className="text-xs font-bold tracking-widest text-primary uppercase mb-4">Live crawler activity</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-5 max-w-2xl">
              <span className="text-primary">#</span> Show teams what AI visibility looks like when it is actually happening
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mb-6">
              Legible customers can see which AI systems are reading their content. Watch crawler names appear in motion and see the kind of visibility the product makes possible.
            </p>

            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-800 mb-2">
                <Radio className="w-4 h-4" />
                Crawler activity stream
              </div>
              <p className="text-sm text-blue-900/80 leading-relaxed">
                A simple, scan-friendly view of which AI systems are reading content, which pages they touch, and how visible your site is becoming across the AI ecosystem.
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-[#08111f] text-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Activity className="w-4 h-4 text-blue-300" />
                  AI crawler activity
                </div>
                <p className="text-xs text-blue-200/60 mt-1">Crawler names and page reads in one simple view</p>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-blue-100">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Live view
              </div>
            </div>

            <div className="px-4 py-4 space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Bot className="w-4 h-4 text-blue-300" />
                        <span className="text-sm font-semibold text-white">{item.crawler}</span>
                        <Dot className="w-4 h-4 text-blue-300/70 -mx-1" />
                        <span className="text-xs uppercase tracking-wider text-blue-200/60">{item.site}</span>
                      </div>
                      <p className="text-sm text-blue-100/90">
                        Read <span className="font-medium text-white">{item.page}</span>
                      </p>
                    </div>

                    <span className="shrink-0 text-xs text-blue-200/60">
                      {item.secondsAgo <= 5 ? "just now" : `${item.secondsAgo}s ago`}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-white/10 bg-white/[0.03]">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-2xl font-display font-bold text-white">20+</div>
                  <div className="text-xs text-blue-200/60">Crawler types tracked</div>
                </div>
                <div>
                  <div className="text-2xl font-display font-bold text-white">24/7</div>
                  <div className="text-xs text-blue-200/60">Visibility monitoring</div>
                </div>
                <div>
                  <div className="text-2xl font-display font-bold text-white">1 view</div>
                  <div className="text-xs text-blue-200/60">for non-technical teams</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
